import React, { useState, useEffect, useRef, useCallback } from "react";
import api from "../services/api";
import { showToast, confirmDelete } from "../helper/notifications";
import { Icon } from "@iconify/react";

const CATEGORIES = [
  { id: "GENERAL", label: "General Media" },
  { id: "GALLERY", label: "Photo Gallery" },
  { id: "LOGO", label: "Brand Logo" },
  { id: "FLOORPLAN", label: "Floorplan / Map" },
  { id: "EXTERIOR", label: "Exterior View" },
  { id: "INTERIOR", label: "Interior View" },
];

const LocationMediaExplorer = ({ locationId, locationData, onMediaChange, isSubLocation = false }) => {
  const [mediaList, setMediaList] = useState([]);
  const [counts, setCounts] = useState({ total: 0, images: 0, videos: 0, documents: 0, logos: 0, floorplans: 0 });
  const [loading, setLoading] = useState(false);
  const [filterType, setFilterType] = useState("ALL"); // 'ALL' | 'IMAGE' | 'VIDEO' | 'LOGO' | 'FLOORPLAN'
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState("grid"); // 'grid' | 'list'

  const mediaBasePath = isSubLocation ? `/sub-locations/${locationId}/media` : `/main-locations/${locationId}/media`;

  // Helper to predict standardized filename for preview
  const sanitizeToken = (str) => {
    if (!str) return '';
    return String(str)
      .trim()
      .replace(/ä/g, 'ae')
      .replace(/Ä/g, 'Ae')
      .replace(/ö/g, 'oe')
      .replace(/Ö/g, 'Oe')
      .replace(/ü/g, 'ue')
      .replace(/Ü/g, 'Ue')
      .replace(/ß/g, 'ss')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-zA-Z0-9_-]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .replace(/-+/g, '-');
  };

  const getPredictedStandardizedName = (originalFileName, index = 0) => {
    const ext = originalFileName ? originalFileName.substring(originalFileName.lastIndexOf('.')).toLowerCase() : '';
    if (isSubLocation) {
      const parentName = sanitizeToken(locationData?.parent_location_name || locationData?.parent_main_name) || 'Main';
      const unitName = sanitizeToken(locationData?.name) || `Unit-${locationId || '1'}`;
      const base = `${parentName}_${unitName}`;
      return index === 0 ? `${base}${ext}` : `${base}_${index}${ext}`;
    }
    const internalId = sanitizeToken(locationData?.internal_id) || `INT-${locationId || 'ID'}`;
    const storeCode = sanitizeToken(locationData?.store_code) || `LQ-LOC-${locationId || '001'}`;
    const locName = sanitizeToken(locationData?.location_name) || `Location-${locationId || '1'}`;
    const base = `${internalId}_${storeCode}_${locName}`;
    return index === 0 ? `${base}${ext}` : `${base}_${index}${ext}`;
  };

  // Upload Queue State
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadCategory, setUploadCategory] = useState("GENERAL");
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  // Lightbox Modal State
  const [lightboxIndex, setLightboxIndex] = useState(null);

  // Fetch Media for Location
  const fetchMedia = useCallback(async () => {
    if (!locationId) return;
    setLoading(true);
    try {
      const res = await api.get(mediaBasePath);
      if (res.data?.success) {
        setMediaList(res.data.data || []);
        if (res.data.counts) setCounts(res.data.counts);
        if (onMediaChange) onMediaChange(res.data.data || []);
      }
    } catch (err) {
      console.error("Error fetching media:", err);
      showToast("error", "Failed to load location media.");
    } finally {
      setLoading(false);
    }
  }, [locationId, onMediaChange]);

  useEffect(() => {
    fetchMedia();
  }, [fetchMedia]);

  // Format File Size
  const formatBytes = (bytes) => {
    if (!bytes || bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
  };

  // Format Date
  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    } catch {
      return String(dateStr);
    }
  };

  // Drag and Drop Handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFilesAdded(Array.from(e.dataTransfer.files));
    }
  };

  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFilesAdded(Array.from(e.target.files));
    }
  };

  const handleFilesAdded = (files) => {
    const validFiles = files.filter((file) => {
      const isImg = file.type.startsWith("image/");
      const isVid = file.type.startsWith("video/");
      const isPdf = file.type === "application/pdf";
      return isImg || isVid || isPdf;
    });

    if (validFiles.length < files.length) {
      showToast("warning", "Some unsupported files were skipped. Supported: Images, Videos, PDFs.");
    }

    // Attach local preview URLs
    const prepared = validFiles.map((file) => ({
      file,
      preview: file.type.startsWith("image/") ? URL.createObjectURL(file) : null,
      name: file.name,
      size: file.size,
      type: file.type,
      category: uploadCategory,
    }));

    setSelectedFiles((prev) => [...prev, ...prepared]);
  };

  const removeQueuedFile = (index) => {
    setSelectedFiles((prev) => {
      const updated = [...prev];
      if (updated[index]?.preview) URL.revokeObjectURL(updated[index].preview);
      updated.splice(index, 1);
      return updated;
    });
  };

  const clearQueue = () => {
    selectedFiles.forEach((f) => {
      if (f.preview) URL.revokeObjectURL(f.preview);
    });
    setSelectedFiles([]);
    setUploadProgress(0);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // Upload Files to Backend & GCS
  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;
    if (!locationId) {
      showToast("warning", "Please save this location first before uploading media.");
      return;
    }

    setIsUploading(true);
    setUploadProgress(10);

    const formData = new FormData();
    selectedFiles.forEach((item) => {
      formData.append("files", item.file);
    });
    formData.append("category", uploadCategory);

    try {
      const res = await api.post(`${mediaBasePath}/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percentCompleted);
          }
        },
      });

      if (res.data?.success) {
        showToast("success", res.data.message || "Files successfully uploaded to Google Cloud Storage!");
        clearQueue();
        fetchMedia();
      } else {
        showToast("error", res.data?.message || "Upload failed.");
      }
    } catch (err) {
      console.error("Upload error:", err);
      showToast("error", err.response?.data?.message || "Failed to upload media files.");
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  // Delete Media Item
  const handleDelete = (item) => {
    confirmDelete(item.original_name || item.file_name, async () => {
      try {
        const res = await api.delete(`/locations-media/${item.id}`);
        if (res.data?.success) {
          showToast("success", "Media file deleted from Cloud Storage.");
          fetchMedia();
        } else {
          showToast("error", res.data?.message || "Failed to delete file.");
        }
      } catch (err) {
        console.error("Delete error:", err);
        showToast("error", "Error deleting file.");
      }
    });
  };

  // Copy Public Link
  const handleCopyLink = (url) => {
    if (!url) return;
    navigator.clipboard.writeText(url);
    showToast("success", "Public Cloud Storage URL copied to clipboard!");
  };

  // Filtered List
  const filteredMedia = mediaList.filter((item) => {
    // Filter Type
    if (filterType === "IMAGE" && item.media_type !== "IMAGE") return false;
    if (filterType === "VIDEO" && item.media_type !== "VIDEO") return false;
    if (filterType === "LOGO" && item.category !== "LOGO") return false;
    if (filterType === "FLOORPLAN" && item.category !== "FLOORPLAN") return false;

    // Search Query
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const matchName = item.original_name?.toLowerCase().includes(q);
      const matchCaption = item.caption?.toLowerCase().includes(q);
      const matchCat = item.category?.toLowerCase().includes(q);
      return matchName || matchCaption || matchCat;
    }

    return true;
  });

  // Lightbox Navigation
  const openLightbox = (index) => setLightboxIndex(index);
  const closeLightbox = () => setLightboxIndex(null);
  const nextMedia = () => {
    if (lightboxIndex === null) return;
    setLightboxIndex((prev) => (prev + 1) % filteredMedia.length);
  };
  const prevMedia = () => {
    if (lightboxIndex === null) return;
    setLightboxIndex((prev) => (prev - 1 + filteredMedia.length) % filteredMedia.length);
  };

  const currentLightboxItem = lightboxIndex !== null ? filteredMedia[lightboxIndex] : null;

  return (
    <div className="location-media-explorer">
      
      {/* 1. TOP STATS & BUCKET INFO BANNER */}
      <div className="d-flex flex-wrap align-items-center justify-content-between p-16 radius-12 bg-neutral-50 border mb-20 gap-3">
        <div className="d-flex align-items-center gap-3">
          <div className="w-44-px h-44-px bg-primary-100 text-primary-600 radius-10 d-flex align-items-center justify-content-center">
            <Icon icon="solar:gallery-wide-bold-duotone" className="text-2xl" />
          </div>
          <div>
            <h6 className="fw-bold mb-1 text-dark d-flex align-items-center gap-2">
              <span>Location Media & Files</span>
              <span className="badge bg-primary-50 text-primary-600 border border-primary-200 px-8 py-3 radius-6 text-2xs fw-semibold">
                gs://media-loqation-360
              </span>
            </h6>
            <p className="text-secondary-light text-xs mb-0">
              High-resolution images and videos stored securely in Google Cloud Storage.
            </p>
          </div>
        </div>

        {/* STAT BADGES */}
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <span className="badge bg-white text-secondary-dark border px-12 py-6 radius-8 d-flex align-items-center gap-1 text-xs">
            <Icon icon="solar:folder-with-files-bold" className="text-primary-600" />
            <span>Total: <strong>{counts.total}</strong></span>
          </span>
          <span className="badge bg-white text-secondary-dark border px-12 py-6 radius-8 d-flex align-items-center gap-1 text-xs">
            <Icon icon="solar:camera-bold" className="text-success-main" />
            <span>Images: <strong>{counts.images}</strong></span>
          </span>
          <span className="badge bg-white text-secondary-dark border px-12 py-6 radius-8 d-flex align-items-center gap-1 text-xs">
            <Icon icon="solar:videocamera-record-bold" className="text-purple-600" />
            <span>Videos: <strong>{counts.videos}</strong></span>
          </span>
          <button
            type="button"
            onClick={fetchMedia}
            className="btn btn-sm btn-outline-secondary radius-8 px-10 py-6 d-flex align-items-center gap-1"
            title="Refresh Media List"
            disabled={loading}
          >
            <Icon icon={loading ? "line-md:loading-twotone-loop" : "solar:refresh-linear"} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* 2. STATE OF THE ART DRAG & DROP UPLOAD ZONE */}
      <div className="card radius-12 border bg-white mb-24 shadow-xs overflow-hidden">
        <div className="card-body p-20">
          
          <div className="d-flex flex-wrap align-items-center justify-content-between mb-16 gap-2">
            <div className="d-flex align-items-center gap-2">
              <Icon icon="solar:cloud-upload-bold-duotone" className="text-primary-600 text-xl" />
              <span className="fw-bold text-sm text-dark">Upload New Images & Videos</span>
            </div>

            {/* CATEGORY SELECTOR */}
            <div className="d-flex align-items-center gap-2">
              <label className="text-xs text-secondary-light fw-medium mb-0">Category:</label>
              <select
                className="form-select form-select-sm radius-8 border text-xs fw-semibold text-secondary-dark"
                value={uploadCategory}
                onChange={(e) => setUploadCategory(e.target.value)}
                style={{ width: "160px" }}
                disabled={isUploading}
              >
                {CATEGORIES.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* DROPZONE AREA */}
          <div
            className={`border border-2 border-dashed radius-12 p-28 text-center transition-all pointer position-relative ${
              isDragging ? "bg-primary-50 border-primary-600 shadow-sm" : "bg-neutral-50 hover-bg-neutral-100 border-neutral-300"
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            style={{ cursor: "pointer" }}
          >
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileInputChange}
              multiple
              accept="image/*,video/*,application/pdf"
              className="d-none"
            />

            <div className="w-56-px h-56-px bg-white text-primary-600 radius-circle d-flex align-items-center justify-content-center mx-auto mb-12 shadow-xs border">
              <Icon icon="solar:upload-track-2-bold-duotone" className="text-3xl" />
            </div>

            <h6 className="fw-bold text-dark text-sm mb-4">
              {isDragging ? "Drop your files here!" : "Drag & Drop images or videos here, or Browse"}
            </h6>
            <p className="text-secondary-light text-xs mb-0">
              Supports JPG, PNG, WEBP, SVG, MP4, WEBM, MOV, PDF (up to 100MB per file)
            </p>
          </div>

          {/* QUEUED FILES PREVIEW & UPLOAD BAR */}
          {selectedFiles.length > 0 && (
            <div className="mt-16 p-16 radius-10 bg-neutral-50 border">
              <div className="d-flex align-items-center justify-content-between mb-12">
                <span className="text-xs fw-bold text-dark">
                  Ready to Upload ({selectedFiles.length} file{selectedFiles.length > 1 ? "s" : ""}):
                </span>
                <button
                  type="button"
                  onClick={clearQueue}
                  className="btn btn-sm btn-text text-danger-main text-xs p-0 border-0"
                  disabled={isUploading}
                >
                  Clear Queue
                </button>
              </div>

              {/* THUMBNAIL LIST */}
              <div className="d-flex gap-2 overflow-x-auto pb-8 mb-12">
                {selectedFiles.map((item, idx) => {
                  const targetName = getPredictedStandardizedName(item.name, idx);
                  return (
                    <div
                      key={idx}
                      className="position-relative bg-white border radius-8 p-6 flex-shrink-0"
                      style={{ width: "160px" }}
                    >
                      {item.preview ? (
                        <img
                          src={item.preview}
                          alt="preview"
                          className="w-100 h-64-px object-fit-cover radius-6 mb-4"
                        />
                      ) : (
                        <div className="w-100 h-64-px bg-neutral-100 radius-6 d-flex align-items-center justify-content-center mb-4 text-secondary-light">
                          <Icon
                            icon={item.type.startsWith("video/") ? "solar:videocamera-bold" : "solar:file-bold"}
                            className="text-2xl"
                          />
                        </div>
                      )}
                      <span
                        className="d-block text-2xs fw-semibold text-dark text-truncate"
                        title={`Original: ${item.name}`}
                      >
                        {item.name}
                      </span>
                      <span
                        className="d-block text-3xs text-primary-600 fw-medium text-truncate mt-1"
                        title={`Standardized: ${targetName}`}
                      >
                        ➔ {targetName}
                      </span>
                      <span className="text-3xs text-secondary-light d-block mt-1">{formatBytes(item.size)}</span>

                      {/* Remove button */}
                      {!isUploading && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeQueuedFile(idx);
                          }}
                          className="position-absolute top-0 end-0 bg-danger-main text-white border-0 radius-circle d-flex align-items-center justify-content-center"
                          style={{ width: "18px", height: "18px", transform: "translate(30%, -30%)" }}
                          title="Remove file"
                        >
                          <Icon icon="solar:close-circle-bold" className="text-xs" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* PROGRESS BAR */}
              {isUploading && (
                <div className="mb-12">
                  <div className="d-flex justify-content-between text-2xs fw-semibold mb-4 text-secondary-dark">
                    <span>Uploading to Google Cloud Storage...</span>
                    <span>{uploadProgress}%</span>
                  </div>
                  <div className="progress" style={{ height: "6px" }}>
                    <div
                      className="progress-bar bg-primary-600 progress-bar-striped progress-bar-animated"
                      role="progressbar"
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                </div>
              )}

              {/* ACTION BUTTON */}
              <div className="d-flex justify-content-end">
                <button
                  type="button"
                  onClick={handleUpload}
                  disabled={isUploading}
                  className="btn btn-primary-600 radius-8 px-20 py-8 text-xs fw-semibold d-flex align-items-center gap-2 shadow-sm"
                >
                  {isUploading ? (
                    <>
                      <Icon icon="line-md:loading-twotone-loop" />
                      <span>Uploading ({uploadProgress}%)...</span>
                    </>
                  ) : (
                    <>
                      <Icon icon="solar:cloud-upload-bold" />
                      <span>Start Upload to Cloud Storage</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

        </div>
      </div>

      {/* 3. FILE EXPLORER TOOLBAR (FILTERS, SEARCH, VIEW MODE) */}
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-16">
        
        {/* FILTER PILLS */}
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <button
            type="button"
            className={`btn btn-sm radius-8 px-12 py-6 text-xs fw-semibold border-0 ${
              filterType === "ALL" ? "bg-primary-600 text-white shadow-xs" : "bg-neutral-100 text-secondary-dark hover-bg-neutral-200"
            }`}
            onClick={() => setFilterType("ALL")}
          >
            All Media ({counts.total})
          </button>
          <button
            type="button"
            className={`btn btn-sm radius-8 px-12 py-6 text-xs fw-semibold border-0 ${
              filterType === "IMAGE" ? "bg-primary-600 text-white shadow-xs" : "bg-neutral-100 text-secondary-dark hover-bg-neutral-200"
            }`}
            onClick={() => setFilterType("IMAGE")}
          >
            Images ({counts.images})
          </button>
          <button
            type="button"
            className={`btn btn-sm radius-8 px-12 py-6 text-xs fw-semibold border-0 ${
              filterType === "VIDEO" ? "bg-primary-600 text-white shadow-xs" : "bg-neutral-100 text-secondary-dark hover-bg-neutral-200"
            }`}
            onClick={() => setFilterType("VIDEO")}
          >
            Videos ({counts.videos})
          </button>
          <button
            type="button"
            className={`btn btn-sm radius-8 px-12 py-6 text-xs fw-semibold border-0 ${
              filterType === "LOGO" ? "bg-primary-600 text-white shadow-xs" : "bg-neutral-100 text-secondary-dark hover-bg-neutral-200"
            }`}
            onClick={() => setFilterType("LOGO")}
          >
            Logos ({counts.logos})
          </button>
          <button
            type="button"
            className={`btn btn-sm radius-8 px-12 py-6 text-xs fw-semibold border-0 ${
              filterType === "FLOORPLAN" ? "bg-primary-600 text-white shadow-xs" : "bg-neutral-100 text-secondary-dark hover-bg-neutral-200"
            }`}
            onClick={() => setFilterType("FLOORPLAN")}
          >
            Floorplans ({counts.floorplans})
          </button>
        </div>

        {/* SEARCH & VIEW SWITCHER */}
        <div className="d-flex align-items-center gap-2 ms-auto">
          <div className="position-relative" style={{ width: "220px" }}>
            <input
              type="text"
              placeholder="Search media files..."
              className="form-control form-control-sm radius-8 ps-32 text-xs"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <Icon
              icon="solar:magnifer-linear"
              className="position-absolute top-50 translate-middle-y start-0 ms-10 text-secondary-light"
            />
          </div>

          <div className="btn-group btn-group-sm radius-8 border overflow-hidden">
            <button
              type="button"
              className={`btn px-10 ${viewMode === "grid" ? "btn-primary" : "btn-light text-secondary-dark"}`}
              onClick={() => setViewMode("grid")}
              title="Grid View"
            >
              <Icon icon="solar:widget-4-bold" />
            </button>
            <button
              type="button"
              className={`btn px-10 ${viewMode === "list" ? "btn-primary" : "btn-light text-secondary-dark"}`}
              onClick={() => setViewMode("list")}
              title="List View"
            >
              <Icon icon="solar:list-bold" />
            </button>
          </div>
        </div>

      </div>

      {/* 4. MEDIA ITEMS CONTENT (GRID OR LIST) */}
      {loading ? (
        <div className="text-center py-60 bg-neutral-50 radius-12 border">
          <div className="spinner-border text-primary-600 mb-8" role="status"></div>
          <p className="text-secondary-light text-xs mb-0">Loading media files from Cloud Storage...</p>
        </div>
      ) : filteredMedia.length === 0 ? (
        <div className="text-center py-60 bg-neutral-50 radius-12 border">
          <Icon icon="solar:folder-error-bold-duotone" className="text-4xl text-secondary-light mb-8" />
          <h6 className="fw-semibold text-dark text-sm mb-2">No media files found</h6>
          <p className="text-secondary-light text-xs mb-0">
            {searchQuery || filterType !== "ALL"
              ? "No files match the selected filter. Try clearing your search."
              : "Drag and drop images or videos above to upload them to this location."}
          </p>
        </div>
      ) : viewMode === "grid" ? (
        
        /* GRID VIEW */
        <div className="row g-3">
          {filteredMedia.map((item, index) => {
            const isVideo = item.media_type === "VIDEO";

            return (
              <div key={item.id} className="col-xxl-3 col-lg-4 col-sm-6">
                <div className="card radius-12 border bg-white h-100 overflow-hidden shadow-xs hover-shadow-sm transition-all position-relative group">
                  
                  {/* PREVIEW CONTAINER */}
                  <div
                    className="position-relative bg-neutral-100 overflow-hidden pointer d-flex align-items-center justify-content-center"
                    style={{ height: "160px" }}
                    onClick={() => openLightbox(index)}
                  >
                    {isVideo ? (
                      <div className="w-100 h-100 d-flex align-items-center justify-content-center bg-dark text-white position-relative">
                        <video
                          src={item.public_url}
                          className="w-100 h-100 object-fit-cover"
                          preload="metadata"
                        />
                        <div className="position-absolute bg-dark bg-opacity-60 radius-circle w-40-px h-40-px d-flex align-items-center justify-content-center text-white">
                          <Icon icon="solar:play-bold" className="text-xl" />
                        </div>
                      </div>
                    ) : item.media_type === "IMAGE" ? (
                      <img
                        src={item.public_url}
                        alt={item.caption || item.original_name}
                        className="w-100 h-100 object-fit-cover"
                        loading="lazy"
                      />
                    ) : (
                      <div className="text-center p-16 text-secondary-light">
                        <Icon icon="solar:document-bold-duotone" className="text-4xl mb-4 text-primary-600" />
                        <span className="text-xs d-block">{item.mime_type}</span>
                      </div>
                    )}

                    {/* CATEGORY & TYPE BADGES */}
                    <div className="position-absolute top-0 start-0 m-8 d-flex gap-1">
                      <span
                        className={`badge px-8 py-3 radius-4 text-3xs fw-semibold ${
                          isVideo ? "bg-purple-600 text-white" : "bg-primary-600 text-white"
                        }`}
                      >
                        {item.media_type}
                      </span>
                      {item.category && item.category !== "GENERAL" && (
                        <span className="badge bg-dark bg-opacity-75 text-white px-6 py-3 radius-4 text-3xs">
                          {item.category}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* CARD BODY */}
                  <div className="p-12">
                    <h6
                      className="text-xs fw-semibold text-dark text-truncate mb-2"
                      title={item.file_name || item.original_name}
                    >
                      {item.file_name || item.caption || item.original_name}
                    </h6>
                    <div className="d-flex align-items-center justify-content-between text-2xs text-secondary-light mb-8">
                      <span>{formatBytes(item.file_size)}</span>
                      <span>{formatDate(item.created_at)}</span>
                    </div>

                    {/* ACTION BUTTONS */}
                    <div className="d-flex align-items-center justify-content-between pt-8 border-top gap-1">
                      <button
                        type="button"
                        onClick={() => openLightbox(index)}
                        className="btn btn-sm btn-light radius-6 px-8 py-4 text-xs d-flex align-items-center gap-1 text-secondary-dark flex-grow-1 justify-content-center"
                        title="View / Play"
                      >
                        <Icon icon={isVideo ? "solar:play-circle-bold" : "solar:eye-bold"} className="text-sm" />
                        <span>View</span>
                      </button>

                      <button
                        type="button"
                        onClick={() => handleCopyLink(item.public_url)}
                        className="btn btn-sm btn-light radius-6 p-6 text-xs text-secondary-dark"
                        title="Copy Public Link"
                      >
                        <Icon icon="solar:link-linear" className="text-sm" />
                      </button>

                      <a
                        href={item.public_url}
                        target="_blank"
                        rel="noreferrer"
                        download={item.file_name || item.original_name}
                        className="btn btn-sm btn-light radius-6 p-6 text-xs text-secondary-dark"
                        title="Open / Download"
                      >
                        <Icon icon="solar:download-linear" className="text-sm" />
                      </a>

                      <button
                        type="button"
                        onClick={() => handleDelete(item)}
                        className="btn btn-sm btn-light radius-6 p-6 text-xs text-danger-main hover-bg-danger-50"
                        title="Delete from Cloud Storage"
                      >
                        <Icon icon="solar:trash-bin-trash-bold" className="text-sm" />
                      </button>
                    </div>

                  </div>

                </div>
              </div>
            );
          })}
        </div>
      ) : (

        /* LIST VIEW */
        <div className="card radius-12 border bg-white overflow-hidden shadow-xs">
          <div className="table-responsive mb-0">
            <table className="table align-middle table-hover mb-0">
              <thead className="bg-neutral-50 text-secondary-light text-xs text-uppercase fw-bold">
                <tr>
                  <th className="py-12 px-16" style={{ width: "60px" }}>Preview</th>
                  <th className="py-12 px-16">File Name</th>
                  <th className="py-12 px-16">Category</th>
                  <th className="py-12 px-16">Type</th>
                  <th className="py-12 px-16">Size</th>
                  <th className="py-12 px-16">Upload Date</th>
                  <th className="py-12 px-16 text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredMedia.map((item, index) => {
                  const isVideo = item.media_type === "VIDEO";

                  return (
                    <tr key={item.id}>
                      {/* Thumbnail */}
                      <td className="py-8 px-16">
                        <div
                          className="w-44-px h-44-px radius-6 bg-neutral-100 overflow-hidden pointer position-relative d-flex align-items-center justify-content-center"
                          onClick={() => openLightbox(index)}
                        >
                          {isVideo ? (
                            <div className="w-100 h-100 bg-dark text-white d-flex align-items-center justify-content-center">
                              <Icon icon="solar:play-bold" className="text-sm" />
                            </div>
                          ) : item.media_type === "IMAGE" ? (
                            <img
                              src={item.public_url}
                              alt="thumb"
                              className="w-100 h-100 object-fit-cover"
                            />
                          ) : (
                            <Icon icon="solar:document-bold" className="text-primary-600 text-lg" />
                          )}
                        </div>
                      </td>

                      {/* Name */}
                      <td className="py-8 px-16">
                        <span className="fw-semibold text-dark text-xs d-block text-truncate" style={{ maxWidth: "240px" }} title={item.file_name || item.original_name}>
                          {item.file_name || item.original_name}
                        </span>
                        {item.caption && item.caption !== item.file_name && item.caption !== item.original_name && (
                          <span className="text-2xs text-secondary-light">{item.caption}</span>
                        )}
                      </td>

                      {/* Category */}
                      <td className="py-8 px-16">
                        <span className="badge bg-neutral-100 text-secondary-dark px-8 py-4 radius-6 text-2xs">
                          {item.category || "GENERAL"}
                        </span>
                      </td>

                      {/* Type */}
                      <td className="py-8 px-16">
                        <span className={`badge px-8 py-4 radius-6 text-2xs ${
                          isVideo ? "bg-purple-100 text-purple-700" : "bg-primary-50 text-primary-700"
                        }`}>
                          {item.media_type}
                        </span>
                      </td>

                      {/* Size */}
                      <td className="py-8 px-16 text-xs text-secondary-dark">
                        {formatBytes(item.file_size)}
                      </td>

                      {/* Date */}
                      <td className="py-8 px-16 text-xs text-secondary-light">
                        {formatDate(item.created_at)}
                      </td>

                      {/* Actions */}
                      <td className="py-8 px-16 text-end">
                        <div className="d-flex align-items-center justify-content-end gap-1">
                          <button
                            type="button"
                            onClick={() => openLightbox(index)}
                            className="btn btn-sm btn-light radius-6 p-6 text-xs text-secondary-dark"
                            title="Preview"
                          >
                            <Icon icon="solar:eye-bold" className="text-sm" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleCopyLink(item.public_url)}
                            className="btn btn-sm btn-light radius-6 p-6 text-xs text-secondary-dark"
                            title="Copy Link"
                          >
                            <Icon icon="solar:link-linear" className="text-sm" />
                          </button>
                          <a
                            href={item.public_url}
                            target="_blank"
                            rel="noreferrer"
                            download={item.file_name || item.original_name}
                            className="btn btn-sm btn-light radius-6 p-6 text-xs text-secondary-dark"
                            title="Download"
                          >
                            <Icon icon="solar:download-linear" className="text-sm" />
                          </a>
                          <button
                            type="button"
                            onClick={() => handleDelete(item)}
                            className="btn btn-sm btn-light radius-6 p-6 text-xs text-danger-main hover-bg-danger-50"
                            title="Delete"
                          >
                            <Icon icon="solar:trash-bin-trash-bold" className="text-sm" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 5. INTERACTIVE LIGHTBOX PREVIEW MODAL */}
      {currentLightboxItem && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(15, 23, 42, 0.85)", zIndex: 1060, backdropFilter: "blur(6px)" }}
          tabIndex="-1"
          onClick={closeLightbox}
        >
          <div
            className="modal-dialog modal-dialog-centered modal-xl"
            style={{ maxWidth: "1000px" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content radius-16 border-0 shadow-lg overflow-hidden bg-dark text-white">
              
              {/* MODAL HEADER */}
              <div className="modal-header border-secondary px-20 py-12 d-flex align-items-center justify-content-between bg-black bg-opacity-40">
                <div className="d-flex align-items-center gap-2">
                  <span className="badge bg-primary-600 text-white px-8 py-4 radius-6 text-2xs">
                    {currentLightboxItem.media_type}
                  </span>
                  <span className="fw-semibold text-sm text-truncate" style={{ maxWidth: "500px" }} title={currentLightboxItem.file_name || currentLightboxItem.original_name}>
                    {currentLightboxItem.file_name || currentLightboxItem.original_name}
                  </span>
                  <span className="text-secondary-light text-2xs">
                    ({lightboxIndex + 1} of {filteredMedia.length})
                  </span>
                </div>

                <div className="d-flex align-items-center gap-2">
                  <button
                    type="button"
                    onClick={() => handleCopyLink(currentLightboxItem.public_url)}
                    className="btn btn-sm btn-outline-light radius-6 px-10 py-4 text-xs d-flex align-items-center gap-1"
                  >
                    <Icon icon="solar:link-linear" />
                    <span>Copy URL</span>
                  </button>
                  <a
                    href={currentLightboxItem.public_url}
                    target="_blank"
                    rel="noreferrer"
                    download={currentLightboxItem.file_name || currentLightboxItem.original_name}
                    className="btn btn-sm btn-outline-light radius-6 px-10 py-4 text-xs d-flex align-items-center gap-1"
                  >
                    <Icon icon="solar:download-linear" />
                    <span>Open / Download</span>
                  </a>
                  <button
                    type="button"
                    className="btn-close btn-close-white ms-2 shadow-none"
                    onClick={closeLightbox}
                  ></button>
                </div>
              </div>

              {/* MODAL BODY (PREVIEW) */}
              <div
                className="modal-body p-0 position-relative d-flex align-items-center justify-content-center bg-black"
                style={{ minHeight: "450px", maxHeight: "75vh" }}
              >
                {/* PREV BUTTON */}
                {filteredMedia.length > 1 && (
                  <button
                    type="button"
                    onClick={prevMedia}
                    className="position-absolute start-0 ms-16 btn btn-dark bg-opacity-60 text-white radius-circle p-12 border-0 shadow-lg"
                    style={{ zIndex: 10 }}
                  >
                    <Icon icon="solar:alt-arrow-left-bold" className="text-2xl" />
                  </button>
                )}

                {/* MEDIA DISPLAY */}
                {currentLightboxItem.media_type === "VIDEO" ? (
                  <video
                    src={currentLightboxItem.public_url}
                    controls
                    autoPlay
                    className="w-100 h-100 object-fit-contain"
                    style={{ maxHeight: "70vh" }}
                  />
                ) : currentLightboxItem.media_type === "IMAGE" ? (
                  <img
                    src={currentLightboxItem.public_url}
                    alt={currentLightboxItem.file_name || currentLightboxItem.original_name}
                    className="w-100 h-100 object-fit-contain"
                    style={{ maxHeight: "70vh" }}
                  />
                ) : (
                  <div className="text-center p-40">
                    <Icon icon="solar:document-bold" className="text-6xl text-primary-400 mb-12" />
                    <p className="text-white text-sm mb-0">Document preview not directly supported in viewer.</p>
                  </div>
                )}

                {/* NEXT BUTTON */}
                {filteredMedia.length > 1 && (
                  <button
                    type="button"
                    onClick={nextMedia}
                    className="position-absolute end-0 me-16 btn btn-dark bg-opacity-60 text-white radius-circle p-12 border-0 shadow-lg"
                    style={{ zIndex: 10 }}
                  >
                    <Icon icon="solar:alt-arrow-right-bold" className="text-2xl" />
                  </button>
                )}
              </div>

              {/* MODAL FOOTER */}
              <div className="modal-footer border-secondary px-20 py-10 bg-black bg-opacity-40 d-flex align-items-center justify-content-between text-xs text-secondary-light">
                <div className="d-flex align-items-center gap-3">
                  <span>Size: <strong>{formatBytes(currentLightboxItem.file_size)}</strong></span>
                  <span>MIME: <strong>{currentLightboxItem.mime_type}</strong></span>
                  <span>Category: <strong>{currentLightboxItem.category}</strong></span>
                  <span>Uploaded: <strong>{formatDate(currentLightboxItem.created_at)}</strong></span>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    closeLightbox();
                    handleDelete(currentLightboxItem);
                  }}
                  className="btn btn-sm btn-outline-danger radius-6 px-12 py-4 d-flex align-items-center gap-1"
                >
                  <Icon icon="solar:trash-bin-trash-bold" />
                  <span>Delete Media</span>
                </button>
              </div>

            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default LocationMediaExplorer;

